import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Phone, Mail, MapPin } from 'lucide-react';

// FAQ Knowledge Base
const FAQ_RESPONSES = {
  greetings: {
    keywords: ['hello', 'hi', 'hey', 'namaste', 'good morning', 'good afternoon'],
    response: "Hello! I'm VASUDHA 🌱 (Virtual Assistant for Subtropical Udyan Data, Help & Advisory). I can help you with information about ICAR-CISH. You can ask me about our varieties, technologies, research programs, publications, or contact details."
  },
  about: {
    keywords: ['about', 'what is cish', 'cish', 'institute', 'icar'],
    response: "ICAR-Central Institute for Subtropical Horticulture (CISH) was established in 1972 at Lucknow. We focus on research in subtropical fruits like mango, guava, aonla, bael, and jamun. We develop improved varieties, production technologies, and support farmers through extension programs."
  },
  varieties: {
    keywords: ['variety', 'varieties', 'mango variety', 'guava variety', 'cultivar', 'fruits'],
    response: "We have developed several improved varieties of subtropical fruits:\n\n🥭 Mango: Amrapali, Mallika, Pusa Arunima\n🍐 Guava: Allahabad Safeda, Lalit, Shweta\n🌰 Aonla: NA-7, Kanchan, Krishna\n\nVisit our Varieties page for complete details: /varieties"
  },
  technologies: {
    keywords: ['technology', 'technologies', 'technique', 'cultivation', 'farming'],
    response: "We offer various fruit cultivation technologies:\n\n• High-density planting systems\n• Integrated pest management\n• Canopy management techniques\n• Post-harvest handling\n• Organic farming methods\n\nExplore all technologies at: /technologies"
  },
  research: {
    keywords: ['research', 'project', 'study', 'funded', 'collaboration'],
    response: "ICAR-CISH conducts extensive research in subtropical horticulture. We have ongoing in-house projects and externally funded projects from agencies like NICRA, DBT, and NABARD. We also collaborate with national and international partners.\n\nView our research: /research-funded\nOur collaborations: /collaboration"
  },
  publications: {
    keywords: ['publication', 'report', 'newsletter', 'book', 'document', 'pdf'],
    response: "We publish various research documents:\n\n📄 Annual Reports\n📰 E-Publications\n📚 Newsletters (Udyan Rashmi)\n📖 Technical Bulletins\n\nDownload publications: /publications"
  },
  vksa: {
    keywords: ['vksa', 'viksit krishi', 'farmer training', 'workshop', 'kisan mela'],
    response: "Viksit Krishi Sankalp Abhiyan (VKSA) showcases our outreach programs, farmer training sessions, and technology demonstrations. We conduct regular workshops, kisan melas, and capacity building programs for farmers.\n\nView VKSA activities: /vksa"
  },
  nursery: {
    keywords: ['nursery', 'planting material', 'sapling', 'plant', 'buy'],
    response: "ICAR-CISH provides quality planting material of improved fruit varieties. We supply grafts, saplings, and budded plants to farmers and organizations.\n\nFor nursery services: /nursery"
  },
  contact: {
    keywords: ['contact', 'phone', 'email', 'address', 'location', 'reach'],
    response: "📍 Address:\nICAR-Central Institute for Subtropical Horticulture\nRehmankhera, P.O. Kakori\nLucknow - 226 101, Uttar Pradesh, India\n\n📞 Phone: 0522-2841022, 23\n📧 Email: cish@icar.gov.in\n🌐 Website: www.cish.org.in\n\nView full contact details: /contact"
  },
  jobs: {
    keywords: ['job', 'career', 'vacancy', 'recruitment', 'apply', 'opening'],
    response: "Current job openings and recruitment notifications are posted on our website. You can find details about positions, eligibility, and application process.\n\nCheck jobs: /jobs"
  },
  tenders: {
    keywords: ['tender', 'quotation', 'bid', 'contract'],
    response: "We publish tender notices for various procurement and contract requirements. All active tenders with submission deadlines are available on our website.\n\nView tenders: /tenders"
  },
  director: {
    keywords: ['director', 'head', 'leadership', 'desk'],
    response: "Visit the Director's Desk to read messages from our Director and learn about the institute's vision and mission.\n\nDirector's Desk: /director"
  },
  patent: {
    keywords: ['patent', 'ipr', 'intellectual property', 'copyright'],
    response: "ICAR-CISH has an active ITMU/IPR Cell that manages intellectual property rights. We have filed and granted patents for various technologies, copyrights for mobile apps, and geographical indications.\n\nView IPR details: /itmu-ipr-cell#ipr-protection"
  }
};

const CONTACT_FALLBACK = `I don't have specific information about that. However, you can contact us for detailed assistance:

📞 Phone: 0522-2841022, 23
📧 Email: cish@icar.gov.in
🌐 Website: www.cish.org.in

Or visit our contact page: /contact`;

// Component to render message with clickable links
const MessageContent = ({ text }) => {
  // Split text by URLs (both /page-slug and www.example.com patterns)
  const urlRegex = /(\/[a-z0-9-]+(?:#[a-z0-9-]+)?|https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts = text.split(urlRegex);
  
  return (
    <div className="text-sm whitespace-pre-wrap">
      {parts.map((part, index) => {
        // Check if this part is a URL
        if (part && part.match(urlRegex)) {
          const isExternal = part.startsWith('http') || part.startsWith('www');
          const href = part.startsWith('www') ? `https://${part}` : part;
          
          return (
            <a
              key={index}
              href={href}
              target={isExternal ? "_blank" : "_self"}
              rel={isExternal ? "noopener noreferrer" : ""}
              className="text-blue-600 hover:text-blue-800 underline font-medium"
              onClick={(e) => {
                if (!isExternal) {
                  e.preventDefault();
                  window.location.href = href;
                }
              }}
            >
              {part}
            </a>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </div>
  );
};

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Hello! I'm VASUDHA 🌱. I can help you with information about ICAR-CISH. Feel free to ask about varieties, technologies, research, publications, or contact details!",
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const findResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    // Check each FAQ category
    for (const [category, data] of Object.entries(FAQ_RESPONSES)) {
      if (data.keywords.some(keyword => lowerMessage.includes(keyword))) {
        return data.response;
      }
    }
    
    // No match found - return contact fallback
    return CONTACT_FALLBACK;
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      type: 'user',
      text: inputValue,
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Simulate bot thinking and respond
    setTimeout(() => {
      const botResponse = {
        type: 'bot',
        text: findResponse(inputValue),
        time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, botResponse]);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickQuestions = [
    "What varieties do you have?",
    "How can I contact you?",
    "What is VKSA?",
    "Research programs",
    "Download publications"
  ];

  const handleQuickQuestion = (question) => {
    setInputValue(question);
    setTimeout(() => handleSend(), 100);
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-[#0E6D2B] text-white rounded-full shadow-lg hover:bg-[#0a5a23] transition-all hover:scale-110 z-[9999] flex items-center justify-center"
          aria-label="Open VASUDHA"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
            ?
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-xl shadow-2xl z-[9999] flex flex-col overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-[#0E6D2B] text-white px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold">VASUDHA</h3>
                <p className="text-xs text-white/80">Always here to help</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-2 ${
                    msg.type === 'user'
                      ? 'bg-[#0E6D2B] text-white'
                      : 'bg-white border border-gray-200 text-gray-800'
                  }`}
                >
                  <MessageContent text={msg.text} />
                  <p className={`text-xs mt-1 ${msg.type === 'user' ? 'text-white/70' : 'text-gray-500'}`}>
                    {msg.time}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 1 && (
            <div className="px-4 py-2 border-t bg-white">
              <p className="text-xs text-gray-600 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickQuestions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickQuestion(q)}
                    className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-[#0E6D2B] hover:text-white rounded-full transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t bg-white p-4">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your question..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:border-[#0E6D2B]"
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className="w-10 h-10 bg-[#0E6D2B] text-white rounded-full hover:bg-[#0a5a23] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Powered by ICAR-CISH
            </p>
          </div>
        </div>
      )}
    </>
  );
}
